import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass, Question } from "@phosphor-icons/react";

export default function About() {
  return (
    <div className="App min-h-screen">
      <SEO
        title="About Us · ExactReach"
        description="ExactReach is a B2B data platform built for companies that need targeted prospect information without another long-term subscription."
      />
      <Navbar />

      {/* Hero */}
      <section className="border-b border-border relative">
        <div className="absolute inset-0 grid-lines opacity-[0.35] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-4">About Us</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-6 max-w-3xl">
            Targeted B2B data, without the annual commitment.
          </h1>
          <p className="text-[18px] text-muted-foreground max-w-2xl leading-[1.6]">
            ExactReach is a B2B data platform built for companies that need targeted prospect information without committing to another long-term database subscription.
          </p>
        </div>
      </section>

      {/* Who we are */}
      <section className="py-24 md:py-32 border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-8">
            <Compass size={22} weight="duotone" className="text-primary" />
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold">Who we are</div>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl mb-12 max-w-3xl leading-[1.05]">Who we are.</h2>
          <div className="prose-body max-w-3xl text-foreground space-y-5">
            <p>
              Our team comes from the demand generation and outbound marketing industry, with more than a decade of experience building audiences for B2B campaigns across different industries, markets and buyer profiles.
            </p>
            <p>
              Over the years, we have worked with complex targeting requirements, large account lists, niche job functions, technology-based audiences and difficult-to-source markets. That experience has taught us that finding data is only one part of the process. The real challenge is ensuring that the records match the brief, contain the right contacts and are usable for the intended campaign.
            </p>
            <p>
              We combine established data sources with experienced research and quality teams to create audiences based on each customer's specific requirements. Every list is delivered as verified data — freshly re-checked business emails and validated firmographics.
            </p>
            <p className="text-foreground font-medium">
              Our aim is simple: make targeted B2B data easier to request, easier to understand and easier to purchase.
            </p>
          </div>
        </div>
      </section>

      {/* Section divider */}
      <div className="border-b border-border bg-muted/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-6 flex items-center gap-3 text-xs font-mono uppercase tracking-widest-2 text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          <span>The story</span>
          <span className="h-px flex-1 bg-border" />
        </div>
      </div>

      {/* Why we started */}
      <section className="py-24 md:py-32 border-b border-border bg-muted/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-8">
            <Question size={22} weight="duotone" className="text-primary" />
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold">Why we started</div>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl mb-12 max-w-3xl leading-[1.05]">
            Why we started ExactReach.
          </h2>
          <div className="prose-body max-w-3xl space-y-5">
            <p>Most B2B data platforms are designed around subscriptions, user seats and monthly credits.</p>
            <p>
              That model works well for companies that prospect every day and have dedicated sales teams using the platform regularly. But it does not work for everyone.
            </p>
            <p>
              Many businesses only need data for a specific campaign, a new market test, a product launch or a short-term outbound initiative. They may need 500 carefully selected contacts today and nothing again for several months.
            </p>
            <p>
              Their choices are often limited. They either purchase a subscription they may not fully use, work with a traditional data seller through long email conversations or spend hours searching, exporting, cleaning and verifying records themselves.
            </p>
            <p className="text-foreground font-medium">We started ExactReach to offer a simpler alternative.</p>
            <p>
              Customers tell us who they want to reach, review how we have understood the requirement, receive an audience estimate and sample, and pay only for the verified data they purchase.
            </p>
            <p className="text-foreground">
              No annual commitment. No compulsory subscription. No confusing credit structure.
            </p>
            <p>Just targeted B2B data, prepared around the audience you actually want to reach.</p>
          </div>

          <div className="mt-14 flex flex-wrap gap-3">
            <Link to="/why-choose-us">
              <Button variant="outline" className="rounded-sm" data-testid="about-see-why-choose">
                See why teams choose us <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
            <Link to="/#query">
              <Button className="rounded-sm" data-testid="about-cta">
                Check my audience <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
